<template>
  <div class="comparison-badge" :class="colorClass" :title="tooltip">
    <v-progress-circular
      v-if="loading"
      indeterminate
      size="12"
      width="2"
    />
    <template v-else-if="hasPrevious">
      <v-icon x-small :color="iconColor">{{ arrowIcon }}</v-icon>
      <span class="comparison-badge__percent">{{ percentString }}</span>
    </template>
    <span v-else class="comparison-badge__empty">–</span>
  </div>
</template>

<script>
import { getDecimalNumberString } from "@/utils";

export default {
  name: "ComparisonBadge",
  props: {
    current: {
      type: Number,
      default: 0,
    },
    previous: {
      type: Number,
      default: null,
    },
    unit: {
      type: String,
      default: "",
    },
    // When true a decrease is "good" (e.g. costs) and gets the positive color.
    invert: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasPrevious() {
      return this.previous !== null && this.previous !== undefined && isFinite(this.previous);
    },
    delta() {
      return this.current - this.previous;
    },
    percent() {
      if (this.previous === 0) {
        return this.current === 0 ? 0 : 100;
      }
      return (this.delta / Math.abs(this.previous)) * 100;
    },
    isPositiveChange() {
      return this.delta >= 0;
    },
    isGood() {
      if (this.delta === 0) return null;
      return this.invert ? this.delta < 0 : this.delta > 0;
    },
    colorClass() {
      if (this.isGood === null) return "comparison-badge--neutral";
      return this.isGood
        ? "comparison-badge--good"
        : "comparison-badge--bad";
    },
    iconColor() {
      if (this.isGood === null) return "grey";
      return this.isGood ? "green" : "red";
    },
    arrowIcon() {
      if (this.delta === 0) return "mdi-minus";
      return this.isPositiveChange ? "mdi-arrow-up" : "mdi-arrow-down";
    },
    percentString() {
      const rounded = Math.round(this.percent);
      const sign = rounded > 0 ? "+" : rounded < 0 ? "-" : "";
      return `${sign}${getDecimalNumberString(Math.abs(rounded), 0)} %`;
    },
    tooltip() {
      if (!this.hasPrevious) return "Kein Vorjahreswert vorhanden";
      const unit = this.unit ? ` ${this.unit}` : "";
      const deltaSign = this.delta >= 0 ? "+" : "-";
      return (
        `Vorjahr: ${getDecimalNumberString(this.previous, 2)}${unit}` +
        ` · Δ ${deltaSign}${getDecimalNumberString(Math.abs(this.delta), 2)}${unit}`
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.comparison-badge {
  display: inline-flex;
  align-items: center;
  gap: 1px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;

  &__percent {
    margin-left: 1px;
  }

  &__empty {
    color: #9e9e9e;
  }

  &--good {
    color: #2e7d32;
    background-color: rgba(67, 160, 71, 0.12);
  }

  &--bad {
    color: #c62828;
    background-color: rgba(215, 0, 0, 0.1);
  }

  &--neutral {
    color: #757575;
    background-color: rgba(0, 0, 0, 0.05);
  }
}
</style>
